import type { ChatPageContext } from '@/api/mutations'
import { chatConversationsQuery, sitesQuery } from '@/api/queries'
import type { ChatConversationSummary, ChatMessage } from '@/api/schemas'
import { ConfirmCardBlockView } from '@/components/sala/blocks/ConfirmCardBlockView'
import { cn } from '@/lib/utils'
import { useSwipeToClose } from '@/lib/useSwipeToClose'
import { useQuery } from '@tanstack/react-query'
import {
  History,
  Maximize2,
  MessageCircle,
  Minimize2,
  PlusCircle,
  RotateCcw,
  Send,
  X, Image as ImageIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import {
  type ChatConversation,
  type ConfirmCardData,
  useChatConversationContext,
} from './useChatConversation'
import { dateLocale, t } from '@/lib/i18n'

const MAX_INPUT_HEIGHT = 96

// WP-polish: matches the Tailwind `lg` breakpoint (1024px) used
// elsewhere in the shell (see AppShell.tsx's `lg:pr-[440px]`). Below
// this width the Assistant is a full-width drawer with a backdrop;
// at or above it, it docks to the right with the page content
// reflowing beside it instead of being covered.
const DESKTOP_QUERY = '(min-width: 1024px)'

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_QUERY).matches : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY)
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

// WP-polish mobile fix: on iOS (Safari AND Chrome-on-iOS -- both are
// WebKit there), a `fixed` element with `top: 0; bottom: 0` does not
// reliably track the actual visible area once the on-screen keyboard
// opens -- the browser's own `window.visualViewport` (height AND
// vertical offset) is the one authoritative source for "what's really
// on screen right now", and CSS alone doesn't get this right across iOS
// versions. Only used for the mobile drawer (`active` is false on the
// desktop docked panel and while fullscreen, where plain CSS is fine).
function useVisualViewportRect(active: boolean): { top: number; height: number } | null {
  const [rect, setRect] = useState<{ top: number; height: number } | null>(null)
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined
    if (!active || !vv) {
      setRect(null)
      return
    }
    const update = () => setRect({ top: vv.offsetTop, height: vv.height })
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [active])
  return rect
}

// WP2A5A: the bubble is plain text everywhere except the single bold
// status flag the Assistant's voice instructions describe (e.g.
// "**Blocked:** Pool work is paused..."). The backend is instructed to
// use that one literal `**...**` pattern and nothing else in the
// bubble, so this only turns `**text**` into bold -- it is not a
// general markdown renderer, and nothing else (`#` headers, `-`/`*`
// lists) is expected or given special treatment here.
const BOLD_SEGMENT_RE = /\*\*([^*]+)\*\*/g

function renderBubbleContent(content: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0
  BOLD_SEGMENT_RE.lastIndex = 0
  // biome-ignore lint/suspicious/noAssignInExpressions: standard exec-loop pattern
  while ((match = BOLD_SEGMENT_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(content.slice(lastIndex, match.index))
    }
    nodes.push(
      <strong key={`b-${key++}`} className="font-semibold">
        {match[1]}
      </strong>,
    )
    lastIndex = BOLD_SEGMENT_RE.lastIndex
  }
  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex))
  }
  return nodes
}

interface SidebarProps {
  /** 12 Oct 2026 (MVP spec): on the site page the chat lives with Zoe,
   *  so the floating launcher is hidden there. */
  hideLauncher?: boolean
  /** CDV-016: AppShell passes true when a sala is mounted (URL carries
   *  ?sala=…). On the false→true transition the sidebar auto-collapses so
   *  the rendered view takes the full viewport. The user can reopen the
   *  chat via the bubble at any time. */
  salaActive?: boolean
  /** WP-polish: open/closed state now lives in AppShell so the main
   *  content area can reserve room for the docked desktop panel (see
   *  AppShell.tsx). This component is otherwise unchanged -- fullscreen
   *  stays local, since it doesn't affect the surrounding layout. */
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ChatSidebar({ salaActive = false, open, onOpenChange, hideLauncher = false }: SidebarProps) {
  const [fullscreen, setFullscreen] = useState(false)
  const conversation = useChatConversationContext()
  const isDesktop = useIsDesktop()

  // CDV-016: collapse when a sala mounts. Only on false→true, so the user
  // is free to reopen while the sala is still active; and we never force
  // a state change in the other direction.
  useEffect(() => {
    if (salaActive) {
      onOpenChange(false)
      setFullscreen(false)
    }
  }, [salaActive, onOpenChange])

  // Zonoo: Assistant Conversation Behavior, item 4 -- re-check the
  // 30-minute inactivity window at the moment the panel is OPENED, not
  // just at page load/scope change. Covers the tab having stayed open
  // the whole time with the panel itself closed.
  const { notePanelOpened } = conversation
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the open transition matters
  useEffect(() => {
    if (open) notePanelOpened()
  }, [open])

  return (
    <>
      {!open && !hideLauncher ? (
        <>
          {/* WP-polish launcher redesign: a plain brand mark read as a
              logo/watermark, not an obviously interactive control, to
              anyone who hasn't used the assistant before. The
              speech-bubble icon (+ "Ask Zonoo" label at desktop widths,
              where there's room for it) is the universal "this opens
              chat" cue. Fixed charcoal/cream regardless of site theme --
              same treatment as the brand's own app-icon asset, which
              doesn't invert in dark mode either. The opened panel's
              header keeps the Z mark (see below), so branding isn't
              lost -- it just isn't what the closed launcher leads with. */}
          <button
            type="button"
            onClick={() => onOpenChange(true)}
            aria-label={t('Ask Zonoo')}
            className="hidden lg:flex fixed bottom-6 right-6 z-40 h-12 items-center gap-2.5 rounded-xl bg-[#292929] px-5 text-[#F7F3EB] shadow-md transition-colors duration-150 hover:bg-[#292929]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#292929] focus-visible:ring-offset-2"
          >
            <MessageCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm font-medium">{t('Ask Zonoo')}</span>
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(true)}
            aria-label={t('Ask Zonoo')}
            className="lg:hidden fixed bottom-4 right-4 z-40 w-14 h-14 rounded-2xl bg-[#292929] text-[#F7F3EB] shadow-md flex items-center justify-center transition-transform duration-150 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#292929] focus-visible:ring-offset-2"
          >
            <MessageCircle className="w-6 h-6" aria-hidden="true" />
          </button>
        </>
      ) : null}

      {/* WP-polish: the dark backdrop (and the modal semantics below) only
          apply on narrow screens, where the Assistant is a full-width
          drawer over the page. At desktop widths it's a docked side
          panel -- the project page stays visible and usable beside it,
          per section 7 of the polish brief. */}
      {open && !fullscreen && !isDesktop ? (
        <button
          type="button"
          aria-label={t('Close chat backdrop')}
          onClick={() => onOpenChange(false)}
          className="fixed inset-0 bg-inverse/40 backdrop-blur-[1px] z-40"
        />
      ) : null}

      {open ? (
        <ChatPanel
          conversation={conversation}
          fullscreen={fullscreen}
          isDesktop={isDesktop}
          onClose={() => {
            onOpenChange(false)
            setFullscreen(false)
          }}
          onToggleFullscreen={() => setFullscreen((v) => !v)}
        />
      ) : null}
    </>
  )
}

interface PanelProps {
  conversation: ChatConversation
  fullscreen: boolean
  isDesktop: boolean
  onClose: () => void
  onToggleFullscreen: () => void
  /** 12 Oct 2026 (Dori): rendered inside Zoe's side panel instead of as
   *  its own floating panel -- no close/fullscreen, no own positioning. */
  embedded?: boolean
  title?: string
}

/** 12 Oct 2026: the chat inside Zoe's panel ("Ask Zoe"). Same
 *  conversation as the floating Assistant, just placed in the panel. */
export function EmbeddedChat({ title = t('Ask Zoe') }: { title?: string }) {
  const conversation = useChatConversationContext()
  const isDesktop = useIsDesktop()
  const { notePanelOpened } = conversation
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the panel opens
  useEffect(() => {
    notePanelOpened()
  }, [])
  return (
    <ChatPanel
      conversation={conversation}
      fullscreen={false}
      isDesktop={isDesktop}
      onClose={() => {}}
      onToggleFullscreen={() => {}}
      embedded
      title={title}
    />
  )
}

/** Human label for the project the conversation is currently scoped to,
 *  sourced from the same page context that is actually sent to the
 *  Assistant (never a guess) -- "All projects" only when that is the
 *  real scope (the portfolio view, which genuinely has no single
 *  project). Returns `null` while the name isn't known yet rather than
 *  showing a placeholder that could be wrong. */
function useProjectContextLabel(pageContext: ChatPageContext): string | null {
  const sitesQ = useQuery(sitesQuery)
  if (pageContext.scope === 'portfolio') return t('All projects')
  const site = sitesQ.data?.sites.find((s) => s.id === pageContext.site_id)
  return site?.name ?? null
}

function ChatPanel({
  conversation,
  fullscreen,
  isDesktop,
  onClose,
  onToggleFullscreen,
  embedded = false,
  title = t('Zonoo Assistant'),
}: PanelProps) {
  const {
    messages,
    streamingText,
    sending,
    historyQ,
    sendMessage,
    newConversation,
    pendingCard,
    pendingViewIntent,
    openPendingViewIntent,
    pageContext,
    draft,
    setDraft,
    isInterrupted,
    isReplyRunning,
    retryLastMessage,
    isHistoricalView,
    openConversation,
  } = conversation
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const messageListRef = useRef<HTMLDivElement | null>(null)
  // 7 Oct 2026: on a phone the chat can be swiped right to close, like the side panels.
  const panelRef = useRef<HTMLElement>(null)
  useSwipeToClose(panelRef, { enabled: !isDesktop && !embedded, onClose })
  const projectLabel = useProjectContextLabel(pageContext)
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false)
  const [retrying, setRetrying] = useState(false)

  // Scroll to bottom when messages change or streaming text grows.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only message length and streaming length matter
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' })
  }, [messages.length, streamingText?.length ?? 0])

  // Focus input on open. WP-polish mobile fix: delayed past the
  // slideInRight animation (0.2s) below -- focusing immediately on
  // mount opens the iOS keyboard (and triggers iOS's own
  // scroll-into-view of the focused input) *while the drawer is still
  // mid-transform*, which is a known way to end up with the drawer
  // settling in the wrong place relative to the visible viewport.
  // Never on desktop, where there's no slide-in and no keyboard fight.
  useEffect(() => {
    if (isDesktop) {
      inputRef.current?.focus()
      return
    }
    // In Zoe's panel on a phone, don't pop the keyboard on open.
    if (embedded) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 260)
    return () => window.clearTimeout(t)
  }, [isDesktop])

  // WP-polish mobile fix: iOS Safari tries to scroll the page to bring
  // a focused input into view, even when that input lives inside a
  // `fixed` element that's already fully on-screen. The underlying
  // project page is tall and scrollable, so Safari scrolls IT instead
  // -- and because a `fixed` element doesn't always stay perfectly
  // pinned through that scroll on iOS, a gap opens up revealing the
  // scrolled page underneath the drawer (reported as the Assistant
  // panel showing "cut off", the page bleeding through around the
  // input).
  //
  // Locking `document.body.style.overflow` alone (the previous attempt
  // here) is a known half-fix: on iOS Safari the scroll/pan that needs
  // blocking often lives on `documentElement` (<html>), not <body>, and
  // even with both locked iOS can still *pan* the visual viewport via
  // touch (a non-scroll gesture `overflow: hidden` doesn't touch) to
  // chase a focused input. So this locks both elements AND adds a
  // touchmove guard that swallows any drag that isn't happening inside
  // the message list or the growing textarea -- the two places that are
  // actually supposed to scroll -- which is the same technique the
  // `body-scroll-lock` library uses for exactly this iOS behaviour.
  // Never on the desktop docked panel -- the page must stay scrollable
  // beside it there (brief §7).
  useEffect(() => {
    if (isDesktop || embedded) return
    const html = document.documentElement
    const body = document.body
    const originalHtmlOverflow = html.style.overflow
    const originalBodyOverflow = body.style.overflow
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'

    const isInsideScrollable = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false
      return Boolean(
        (messageListRef.current && messageListRef.current.contains(target)) ||
          (inputRef.current && inputRef.current.contains(target)),
      )
    }
    const onTouchMove = (e: TouchEvent) => {
      if (!isInsideScrollable(e.target)) e.preventDefault()
    }
    document.addEventListener('touchmove', onTouchMove, { passive: false })

    return () => {
      html.style.overflow = originalHtmlOverflow
      body.style.overflow = originalBodyOverflow
      document.removeEventListener('touchmove', onTouchMove)
    }
  }, [isDesktop])

  // The actual fix for the "panel looks cut off / page shows through"
  // report: track the real visible area via visualViewport and pin the
  // drawer's top/height to it explicitly, instead of trusting `fixed`
  // + `top-0 bottom-0` to do the right thing on iOS once the keyboard
  // is involved.
  const viewportRect = useVisualViewportRect(!isDesktop && !fullscreen && !embedded)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const text = draft.trim()
    if (!text || sending) return
    setDraft('')
    if (inputRef.current) inputRef.current.style.height = 'auto'
    void sendMessage(text)
  }

  const handleRetry = () => {
    if (retrying) return
    setRetrying(true)
    void retryLastMessage().finally(() => setRetrying(false))
  }

  const submitSuggestion = (text: string) => {
    if (sending) return
    void sendMessage(text)
  }

  const inputId = useId()
  const isEmpty = messages.length === 0 && streamingText === null && !historyQ.isPending
  const isProjectScoped = pageContext.scope !== 'portfolio'
  const historySiteId = pageContext.scope === 'portfolio' ? null : pageContext.site_id
  const historyQList = useQuery({
    ...chatConversationsQuery(historySiteId),
    enabled: historyMenuOpen,
  })

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative .showModal which clashes with our state-driven open
    <aside
      ref={panelRef}
      role={isDesktop || embedded ? undefined : 'dialog'}
      aria-modal={isDesktop || embedded ? undefined : true}
      aria-label={title}
      // viewportRect overrides top/height inline once known, taking
      // priority over the top-0/bottom-0 classes below (inline style
      // always wins) -- exactly the "what's really visible right now"
      // rect from visualViewport, immune to iOS's fixed-position/
      // keyboard quirks. Unset (undefined) on desktop and fullscreen,
      // where the plain CSS below is correct and untouched.
      style={
        viewportRect
          ? { top: viewportRect.top, height: viewportRect.height, bottom: 'auto' }
          : undefined
      }
      className={cn(
        embedded
          ? 'relative bg-background flex flex-col overflow-hidden h-full min-h-0'
          : 'fixed bg-background shadow-sm border-l border-border flex flex-col overflow-hidden z-50 animate-[slideInRight_0.2s_ease-out]',
        embedded
          ? ''
          : fullscreen
            ? 'inset-0'
            : 'top-0 right-0 bottom-0 w-full sm:w-[400px] md:w-[440px] border-l border-border',
      )}
    >
      <header
        className={cn(
          'flex items-center justify-between flex-shrink-0 bg-background',
          // 12 Oct 2026 (Dori): in Zoe's panel no second title -- just the
          // history and new-chat icons, top right.
          embedded ? 'justify-end px-3 pt-2' : 'px-4 py-3 border-b border-border',
        )}
      >
        <div className={cn('flex items-center gap-2 min-w-0', embedded && 'hidden')}>
          {/* WP-polish: the approved Z, medium weight, inside a small
              outlined rounded square -- echoes the ".ai" badge frame
              instead of a generic robot icon. */}
          {embedded ? null : (
            <span className="flex-shrink-0 w-7 h-7 rounded-md border border-border flex items-center justify-center bg-card">
              <img src="/brand/zonoo-z-charcoal.png" alt="" aria-hidden="true" className="w-4 h-4" />
            </span>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-sm text-foreground leading-tight">
              {title}
            </div>
            {projectLabel ? (
              <div className="text-xs text-foreground-muted leading-tight truncate">
                {projectLabel}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="relative">
            <button
              type="button"
              onClick={() => setHistoryMenuOpen((v) => !v)}
              title={t('History')}
              aria-label={t('Conversation history')}
              aria-expanded={historyMenuOpen}
              className="text-foreground-muted hover:text-foreground hover:bg-accent rounded p-1 transition-colors"
            >
              <History className="w-4 h-4" />
            </button>
            {historyMenuOpen ? (
              <HistoryMenu
                items={historyQList.data?.conversations ?? []}
                isLoading={historyQList.isPending}
                currentConversationId={conversation.conversationId}
                onSelect={(id) => {
                  openConversation(id)
                  setHistoryMenuOpen(false)
                }}
                onClose={() => setHistoryMenuOpen(false)}
              />
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              newConversation()
              setHistoryMenuOpen(false)
            }}
            title={t('New chat')}
            aria-label={t('New chat')}
            className="text-foreground-muted hover:text-foreground hover:bg-accent rounded p-1 transition-colors"
          >
            <PlusCircle className="w-4 h-4" />
          </button>
          {embedded ? null : (<>
          <button
            type="button"
            onClick={onToggleFullscreen}
            title={t('Toggle fullscreen')}
            aria-label={t('Toggle fullscreen')}
            className="hidden sm:inline-flex text-foreground-muted hover:text-foreground hover:bg-accent rounded p-1 transition-colors"
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            title={t('Close')}
            aria-label={t('Close chat')}
            className="text-foreground-muted hover:text-foreground hover:bg-accent rounded p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          </>)}
        </div>
      </header>

      {isHistoricalView ? (
        <div className="flex items-center justify-between gap-2 px-4 py-1.5 bg-accent/60 border-b border-border-subtle flex-shrink-0">
          <span className="text-xs text-foreground-muted">{t('Viewing an older conversation')}</span>
          <button
            type="button"
            onClick={newConversation}
            className="text-xs font-medium text-primary hover:underline flex-shrink-0"
          >
            {t('Start new chat')}
          </button>
        </div>
      ) : null}

      <div ref={messageListRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {isEmpty ? (
          <EmptyState isProjectScoped={isProjectScoped} onSuggestion={submitSuggestion} chipsOnly={embedded} />
        ) : (
          messages.map((m, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: messages have no stable id and order is append-only
            <Bubble
              key={i}
              speaker={m.role}
              content={m.content}
              timestamp={m.created_at}
              // WP1B: the card belongs to the LATEST assistant turn only
              // (streamingText === null means that turn has finished —
              // otherwise a new one is already in flight and the old
              // card, if any, is stale).
              //
              // Contractor-update workflow fix: pendingCard only exists
              // for the live session (it resets to null on every fresh
              // mount — see its own comment in useChatConversation.ts).
              // After a reopen/refresh, fall back to this message's own
              // `confirm_card` — restored from chat_messages by
              // /api/chat/history — so the card doesn't just vanish.
              card={
                i === messages.length - 1 && m.role === 'assistant' && streamingText === null
                  ? (pendingCard ?? m.confirm_card ?? undefined)
                  : undefined
              }
              onOpenView={
                i === messages.length - 1 && m.role === 'assistant' && streamingText === null
                  ? pendingViewIntent
                    ? openPendingViewIntent
                    : undefined
                  : undefined
              }
            />
          ))
        )}
        {streamingText !== null ? (
          streamingText.length === 0 ? (
            <ThinkingDots />
          ) : (
            <Bubble speaker="assistant" content={streamingText} streaming />
          )
        ) : null}
        {isReplyRunning ? (
          // Item 4 ("is still running: show its actual progress
          // state"): the server confirms this exact question is still
          // being worked on right now (e.g. from another tab, or this
          // one reconnected mid-request) -- distinct from a genuine
          // interruption, so no Retry here (one is already running;
          // sending another would risk a duplicate paid call).
          <div className="flex flex-col items-start gap-1.5">
            <div className="rounded-xl px-3 py-2 max-w-[85%] text-sm bg-card text-foreground-muted italic rounded-tl-sm border border-border-subtle">
              {t('Still working on this reply…')}
            </div>
          </div>
        ) : null}
        {isInterrupted ? (
          <div className="flex flex-col items-start gap-1.5">
            <div className="rounded-xl px-3 py-2 max-w-[85%] text-sm bg-card text-foreground-muted italic rounded-tl-sm border border-border-subtle">
              {t('The reply was interrupted. Try again.')}
            </div>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border text-foreground hover:border-primary transition-colors disabled:opacity-60"
            >
              <RotateCcw className="w-3 h-3" />
              {retrying ? t('Retrying…') : t('Retry')}
            </button>
          </div>
        ) : null}
        <div ref={messagesEndRef} />
      </div>

      {draft && !sending ? (
        // Item 5 ("offer a small 'Resume draft' option ... without
        // automatically restoring the old conversation"): the draft
        // text itself is always preserved and already sits in the
        // input below (see useChatConversation's draft/setDraft), but
        // that alone gives no indication anything was restored -- this
        // small, dismissible strip makes an unsent draft explicit
        // (whether just typed or carried over from a previous visit)
        // and gives a one-tap way to discard it instead of sending it.
        <div className="flex items-center justify-between px-3 py-1 text-xs text-foreground-muted border-t border-border-subtle bg-muted/40 flex-shrink-0">
          <span>{t('Unsent draft')}</span>
          <button
            type="button"
            onClick={() => setDraft('')}
            className="underline hover:text-foreground"
          >
            {t('Clear')}
          </button>
        </div>
      ) : null}
      <form
        onSubmit={handleSubmit}
        className="border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2 flex-shrink-0"
      >
        <label htmlFor={inputId} className="sr-only">
          {t('Message')}
        </label>
        <textarea
          id={inputId}
          ref={inputRef}
          rows={1}
          value={draft}
          placeholder={t('Type a message…')}
          onChange={(e) => {
            setDraft(e.currentTarget.value)
            const el = e.currentTarget
            el.style.height = 'auto'
            el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit(e as unknown as React.FormEvent)
            }
          }}
          className="flex-1 resize-none bg-card text-foreground placeholder:text-foreground-muted border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary max-h-24 overflow-y-auto"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          aria-label={t('Send')}
          className="bg-primary hover:bg-primary-hover text-primary-foreground rounded-lg px-3 py-2 transition-colors disabled:bg-disabled disabled:text-foreground-muted disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </aside>
  )
}

/** WP-polish (section 6): a plain greeting for the portfolio-wide
 *  conversation, or -- for a conversation scoped to one project -- a
 *  project-specific prompt plus two suggestions that submit ordinary
 *  questions through the existing send flow. Neither path invents any
 *  answer; both just seed the same text input a person could type. */
/** Zonoo: Assistant Conversation Behavior -- History dropdown. A plain
 *  list (title + relative date) of the caller's own conversations in
 *  the CURRENT project scope, most-recently-active first (the query
 *  itself is already scoped and ordered server-side). Closes on an
 *  outside click via a full-screen transparent overlay button, same
 *  pattern as the mobile backdrop above. */
function HistoryMenu({
  items,
  isLoading,
  currentConversationId,
  onSelect,
  onClose,
}: {
  items: ChatConversationSummary[]
  isLoading: boolean
  currentConversationId: string
  onSelect: (conversationId: string) => void
  onClose: () => void
}) {
  return (
    <>
      <button
        type="button"
        aria-label={t('Close history menu')}
        onClick={onClose}
        className="fixed inset-0 z-[55] cursor-default"
      />
      <div className="absolute right-0 top-full mt-1 w-64 max-h-80 overflow-y-auto rounded-lg border border-border bg-background shadow-lg z-[60] py-1">
        {isLoading ? (
          <div className="px-3 py-2 text-xs text-foreground-muted">{t('Loading…')}</div>
        ) : items.length === 0 ? (
          <div className="px-3 py-2 text-xs text-foreground-muted">
            {t('No past conversations here yet.')}
          </div>
        ) : (
          items.map((c) => (
            <button
              key={c.conversation_id}
              type="button"
              onClick={() => onSelect(c.conversation_id)}
              className={cn(
                'w-full text-left px-3 py-2 hover:bg-accent transition-colors',
                c.conversation_id === currentConversationId ? 'bg-accent' : '',
              )}
            >
              <div className="text-sm text-foreground truncate">
                {c.title || t('Untitled conversation')}
              </div>
              {c.updated_at ? (
                <div className="text-[10px] text-subtle-foreground mt-0.5 tabular-nums">
                  {formatChatTime(c.updated_at)}
                </div>
              ) : null}
            </button>
          ))
        )}
      </div>
    </>
  )
}

function EmptyState({
  isProjectScoped,
  onSuggestion,
  chipsOnly = false,
}: {
  isProjectScoped: boolean
  onSuggestion: (text: string) => void
  /** 12 Oct 2026: in Zoe's panel her header already invites a question,
   *  so no greeting bubble -- just the suggested questions. */
  chipsOnly?: boolean
}) {
  if (!isProjectScoped) {
    return (
      <Bubble
        speaker="assistant"
        content={t("Hello, I'm the Zonoo Assistant. I can help with projects, cameras, alerts and summaries. What do you need?")}
      />
    )
  }
  return (
    <div className="flex flex-col items-start gap-2">
      {chipsOnly ? null : <Bubble speaker="assistant" content={t('What would you like to know about this project?')} />}
      <div className="flex flex-wrap gap-2 pl-0">
        {[t("Tell me more about today's work"), t('What needs attention?')].map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => onSuggestion(suggestion)}
            className="text-xs px-3 py-1.5 rounded-lg border border-border text-foreground-muted hover:border-primary hover:text-foreground transition-colors"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  )
}

function Bubble({
  speaker,
  content,
  timestamp,
  streaming = false,
  card,
  onOpenView,
}: {
  speaker: ChatMessage['role']
  content: string
  timestamp?: string
  streaming?: boolean
  // Contractor-update workflow fix: an array so a composed reply
  // reporting several distinct updates in one message can show
  // several cards stacked on this one bubble, not just the first.
  card?: ConfirmCardData[]
  // Zonoo: Assistant Conversation Behavior -- present only when this
  // turn's reply carried a ⟦VIEW⟧ marker that hasn't been opened yet.
  // Clicking it dispatches the held-back intent instead of the view
  // having auto-painted over the screen the instant the reply arrived.
  onOpenView?: () => void
}) {
  // WP1B: a composed reply that is ONLY a confirm_card should show just
  // the card — no separate, redundant preamble bubble above it. The
  // model is instructed not to add one, but render defensively too: an
  // empty (or whitespace-only) text bubble never draws its box. This is
  // also why the Assistant Conversation Behavior item-6 "no visible
  // answer" fix does NOT add an empty-assistant-row placeholder here: a
  // genuine interruption (client disconnect before the assistant reply
  // was ever persisted) never leaves an empty ASSISTANT row -- it leaves
  // no assistant row at all, which useChatConversation's isInterrupted/
  // awaiting_reply already detects and surfaces (see the Retry banner in
  // ChatPanel). An empty assistant row WITH no card is only ever a
  // confirm_card-only historical turn whose card belongs to an earlier,
  // now-superseded turn -- correctly silent, not a bug.
  const hasText = content.trim().length > 0
  return (
    <div className={cn('flex flex-col', speaker === 'user' ? 'items-end' : 'items-start')}>
      {hasText || streaming ? (
        <div
          className={cn(
            'rounded-xl px-3 py-2 max-w-[85%] text-sm whitespace-pre-wrap break-words',
            speaker === 'user'
              ? 'bg-primary text-primary-foreground rounded-tr-sm'
              : 'bg-card text-foreground rounded-tl-sm border border-border-subtle',
          )}
        >
          {renderBubbleContent(content)}
          {streaming ? (
            <span className="inline-block w-1.5 h-3 bg-subtle-strong align-text-bottom ml-0.5 animate-pulse" />
          ) : null}
        </div>
      ) : null}
      {card && card.length > 0 ? (
        <div className={cn('w-[85%] flex flex-col gap-2', hasText ? 'mt-2' : '')}>
          {card.map((c) => (
            <ConfirmCardBlockView
              key={c.id}
              id={c.id}
              text={c.text}
              actions={c.actions}
              payload={c.payload}
            />
          ))}
        </div>
      ) : null}
      {onOpenView ? (
        <button
          type="button"
          onClick={onOpenView}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-semibold text-primary shadow-sm hover:bg-muted transition-colors min-h-[40px]',
            hasText || (card && card.length > 0) ? 'mt-2' : '',
          )}
        >
          <ImageIcon className="w-4 h-4" aria-hidden="true" />
          {t('See the evidence')}
        </button>
      ) : null}
      {(hasText || streaming || (card && card.length > 0) || onOpenView) && timestamp ? (
        <span className="text-[10px] text-subtle-foreground mt-0.5 tabular-nums">
          {formatChatTime(timestamp)}
        </span>
      ) : null}
    </div>
  )
}

function ThinkingDots() {
  return (
    <div className="flex items-center gap-2 text-muted-foreground text-sm">
      <div className="flex gap-1">
        <span
          className="w-1.5 h-1.5 bg-subtle-strong rounded-full animate-bounce"
          style={{ animationDelay: '0ms' }}
        />
        <span
          className="w-1.5 h-1.5 bg-subtle-strong rounded-full animate-bounce"
          style={{ animationDelay: '150ms' }}
        />
        <span
          className="w-1.5 h-1.5 bg-subtle-strong rounded-full animate-bounce"
          style={{ animationDelay: '300ms' }}
        />
      </div>
    </div>
  )
}

function formatChatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) {
    return d.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString(dateLocale(), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
